import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { Typography } from '@mui/material';
import { useForm } from 'react-hook-form';
import { questions } from './config';
import { ScreeningModuleStore } from 'Core_Pages/Screening/ScreeningModuleStore';
import { StyledCircularProgress } from 'Core_Components/LoadingWrapper/styles';
import ErrorModal from 'Core_Components/ErrorModal/ErrorModal';
import { NeedsWrapper, NeedsProgressBar, StyledBookmark, StyledNeedsForm } from './styles';
import { displayDarkStatusBar } from 'Core_Helpers/statusBar';
import { epdsQuestionMap, EPDSQuestionType } from './epdsQuestionType';

// Define card colors array similar to AdultNeeds implementation
const cardColors = [
  '#565c89',
  '#67973E',
  '#008A8A',
  '#24215D',
  '#8E1460',
  '#C7770E',
  '#007874',
  '#393E83',
  '#67973E',
  '#555B8A',
];

function AdultEPDS() {
  const intl = useIntl();
  const screeningStore = ScreeningModuleStore.Instance().Use();
  const screeningTakerStore = screeningStore.ScreeningForm.Use();
  const { control } = useForm();
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const isLoading = screeningTakerStore.IsLoading || screeningStore.IsLoading;
  const hasError = screeningStore.ScreeningHasError;
  const numberOfScreeningsLeft = screeningStore.DueScreenings?.length > 0 ? screeningStore.DueScreenings?.length : 1;
  const currentScreeningIndex = screeningStore.DueScreeningsIndex + 1;

  const onCloseErrorModal = () => screeningStore.acknowledgeError();
  const onAnswer = (question, answer) => {
    const nextQuestion = currentQuestion + 1;

    // Skip updating answer for intro component
    if (questions[currentQuestion]?.questionType !== EPDSQuestionType.INTRO) {
      screeningTakerStore.updateAnswer(question, answer);
    }

    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(nextQuestion);
    }

    if (screeningTakerStore.IsValid) {
      screeningTakerStore.complete();
    }
  };

  useEffect(() => {
    displayDarkStatusBar();
  }, []);

  if (isLoading) {
    return <StyledCircularProgress />;
  }

  const Component = epdsQuestionMap[questions[currentQuestion]?.questionType];
  const filteredOptions = questions[currentQuestion]?.options.filter((o) => o.textId !== 'common.none');

  return (
    <>
      <NeedsWrapper fullScreen open={true} aria-labelledby="screening-title">
        <StyledBookmark>
          {`${currentScreeningIndex} ${intl.formatMessage({ id: 'common.of' })} ${numberOfScreeningsLeft}`}
        </StyledBookmark>
        <NeedsProgressBar variant="determinate" value={(currentQuestion / questions.length) * 100} />
        <Typography id="screening-title" component="h1">
          {intl.formatMessage({ id: 'screenings.adult-epds' })}
        </Typography>
        <StyledNeedsForm cardColor={cardColors[currentQuestion % cardColors.length]}>
          <div className="question-card">
            <Typography className="question-text">
              {intl.formatMessage({ id: questions[currentQuestion]?.textId })}
            </Typography>
            <div className="answer-container">
              <Component
                control={control}
                text=""
                answerType={questions[currentQuestion]?.answerType}
                filteredOptions={filteredOptions.map((option) => ({
                  ...option,
                  text: intl.formatMessage({ id: option.textId }),
                }))}
                onChange={(answer) => {
                  const answerOptionGuid = answer.currentTarget?.name;
                  onAnswer(questions[currentQuestion]?.name, answerOptionGuid ? answerOptionGuid : answer);
                }}
                {...questions[currentQuestion]}
              />
            </div>
          </div>
        </StyledNeedsForm>
      </NeedsWrapper>
      <ErrorModal open={hasError} onClose={onCloseErrorModal} />
    </>
  );
}

export default AdultEPDS;
